import React, { useEffect, useMemo, useState } from "react";
import "./questionAdminTour.css";

const PAD = 8;
const MARGIN = 16;
const GAP = 14;
const TOOLTIP_W = 320;
// Conservative — must match (or exceed) the CSS max-height on .qa-tour-tooltip
// so the clamp below can never place a tooltip partly off-screen.
const TOOLTIP_H = 320;

function clampRange(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Minimal spotlight-style guided tour: highlights one `data-tour="..."` target
 * at a time with a dimmed backdrop + positioned tooltip. No external
 * dependency — steps are plain data, so callers just describe what to show.
 */
export default function QuestionAdminTour({ steps, active, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (active) setStepIndex(0);
  }, [active]);

  const step = active ? steps[stepIndex] : null;

  useEffect(() => {
    if (!step) {
      setRect(null);
      return undefined;
    }

    let cancelled = false;

    const measure = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        // Target isn't rendered (e.g. category tabs hidden) — move along.
        if (!cancelled) {
          setStepIndex((i) => (i < steps.length - 1 ? i + 1 : i));
        }
        return;
      }
      const r = el.getBoundingClientRect();
      if (!cancelled) {
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };

    const el = document.querySelector(step.target);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });

    const t = setTimeout(measure, el ? 260 : 0);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, stepIndex]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, onClose]);

  // Positions fully in px (no CSS transform) and clamps both edges against the
  // viewport, so a tooltip can never render partly off-screen — regardless of
  // where its target sits on a tall/short page.
  const tooltipStyle = useMemo(() => {
    if (!rect || !step) return null;
    const placement = step.placement || "bottom";
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tw = Math.min(TOOLTIP_W, vw - MARGIN * 2);
    const th = Math.min(TOOLTIP_H, vh - MARGIN * 2);

    let top = rect.top;
    let left = rect.left;

    if (placement === "top") {
      top = rect.top - GAP - th;
    } else if (placement === "bottom") {
      top = rect.top + rect.height + GAP;
    }

    if (placement === "left") {
      left = rect.left - GAP - tw;
    } else if (placement === "right") {
      left = rect.left + rect.width + GAP;
    }

    return {
      position: "fixed",
      top: clampRange(top, MARGIN, vh - MARGIN - th),
      left: clampRange(left, MARGIN, vw - MARGIN - tw),
    };
  }, [rect, step]);

  if (!active || !step) return null;

  const isLast = stepIndex === steps.length - 1;

  return (
    <div className="qa-tour-root" role="dialog" aria-modal="true" aria-label="Guided tour">
      <div className="qa-tour-backdrop" onClick={onClose} />

      {rect && (
        <div
          className="qa-tour-spotlight"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      )}

      {rect && tooltipStyle && (
        <div className="qa-tour-tooltip" style={tooltipStyle}>
          <button type="button" className="qa-tour-close" aria-label="Close tour" onClick={onClose}>
            <i className="fa-solid fa-times" />
          </button>
          <div className="qa-tour-step-count">
            Step {stepIndex + 1} of {steps.length}
          </div>
          <h4 className="qa-tour-title">{step.title}</h4>
          <p className="qa-tour-content">{step.content}</p>
          <div className="qa-tour-footer">
            <button type="button" className="qa-tour-skip" onClick={onClose}>
              Skip tour
            </button>
            <div className="qa-tour-nav">
              {stepIndex > 0 && (
                <button type="button" className="qa-tour-btn qa-tour-btn-outline" onClick={() => setStepIndex((i) => i - 1)}>
                  Back
                </button>
              )}
              <button
                type="button"
                className="qa-tour-btn qa-tour-btn-primary"
                onClick={() => (isLast ? onClose() : setStepIndex((i) => i + 1))}
              >
                {isLast ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
